import { atom } from "jotai";
import { atomWithStorage } from "jotai/utils";
import type { LocationResponse } from "@models/location";
import { MembershipStatus, RoleType } from "@models/user";
import {
    isFetchingLocationsAtom,
    locationsAtom,
    locationsFetchErrorAtom,
    locationsLoadedAtom,
} from "./locationAtoms";
import { currentUserAtom } from "./userAtoms";

// The customer's active shipping destination (#228 / E4-S12). Every storefront product read,
// the cart and the vendor-sku read are answered against it: restricted products drop out of the
// lists and GET /products/{id} comes back with a restriction block and no pricing.

export const SHIP_TO_STORAGE_KEY = "usnara.shipToLocationId";

// Persisted so the choice survives a reload. localStorage ONLY, no URL parameter (Lead ruling
// 1): the switcher sits in the header of every customer page, so a URL half would be honoured
// on the two pages that own their state in the URL and silently dropped on the rest — and a
// location id in a shared link 404s every read for a colleague at another organisation.
// Precedent for the shape: Search/store/searchAtoms.ts recentSearchesAtom.
//
// `getOnInit` is REQUIRED here, unlike recentSearchesAtom. Without it the atom starts at null
// and only picks the stored id up on mount — one commit later — so the first read of a hard
// page load would go out with no destination, be answered against the API's fallback location,
// and then be re-issued when the id landed. Two answers for one page load, the first of them
// for a location the shopper did not choose.
export const storedShipToLocationIdAtom = atomWithStorage<string | null>(
    SHIP_TO_STORAGE_KEY,
    null,
    undefined,
    { getOnInit: true },
);

// Whether the switcher is showing its location list. Follows the app's overlay convention: the
// atom is the single source of truth for visibility, so a surface that is BLOCKED by the current
// destination (the not-available product view, a restricted cart line) can offer "change
// location" without reaching into the header.
export const isShipToSwitcherOpenAtom = atom(false);

// The lifecycle of GET /locations, as the switcher renders it. Four distinct states, and
// `failed` is one of them: a location list that failed to load is not an empty one, and the
// switcher must not render a shopper's destination as "none" because a read broke.
export type ShipToLocationsStatus = "idle" | "loading" | "loaded" | "failed";

// DERIVED from the shared location atoms, not a second machine of its own. Five surfaces issue
// GET /locations into locationsAtom (the switcher, Checkout, OrderConfirmation, AccountLocations,
// RejectedLocations) and useLocationApi.getLocations settles all three of these for every one of
// them. A status only the switcher's own read could settle left the header rendering its error
// for the rest of the session after another surface had loaded the list perfectly well — two
// machines over one request, disagreeing.
//
// Order matters: a list that HAS loaded stays `loaded` — the eligible locations are in the
// atom and the switcher keeps offering them while Checkout, Account or any other surface
// re-reads the list, or has its own read fail (locationsLoadedAtom is set on success and never
// cleared). Before the first success, a retry in flight is `loading`, not still `failed`
// (getLocations clears the error atom as it starts), and a later success anywhere clears
// `failed` for good.
export const shipToLocationsStatusAtom = atom<ShipToLocationsStatus>((get) => {
    if (get(locationsLoadedAtom)) return "loaded";
    if (get(isFetchingLocationsAtom)) return "loading";
    if (get(locationsFetchErrorAtom)) return "failed";

    return "idle";
});

// Claims the right to issue GET /locations for the switcher, returning true only to the caller
// that won. Atomic BECAUSE it is a write atom: the desktop and the mobile nav both mount a
// switcher (one is hidden by a media query, not unmounted), and two effects in the same commit
// both read the pre-commit state — a flag raised inside one of them lags and the second fires
// anyway. Reading and writing inside one write function closes that window.
//
// The claim IS `isFetchingLocationsAtom`, which is what getLocations raises a moment later
// anyway, so a read another surface already has in flight also wins it. A failure is claimable —
// that is what makes the switcher's Retry work; a loaded list is not, so it is read once per
// session and cached in locationsAtom.
export const startShipToLocationsLoadAtom = atom(
    null,
    (get, set): boolean => {
        if (get(isFetchingLocationsAtom) || get(locationsLoadedAtom)) {
            return false;
        }

        set(isFetchingLocationsAtom, true);

        return true;
    },
);

// Display order for a list of the customer's locations: oldest first, then by name, the order
// the API's resolver picks its default in. Display only — the default itself is the row the API
// flags `isDefaultShipTo` (#404), never this list's first row: createdAt is compared as text,
// and ISO strings with different fractional precision sort differently as text than as
// instants ("…00.5Z" before "…00Z"). Exported so the certificate re-upload modal lists the
// customer's locations in the same order the header does.
export const compareByCreatedAtThenName = (
    a: LocationResponse,
    b: LocationResponse,
): number =>
    a.createdAt.localeCompare(b.createdAt) || a.name.localeCompare(b.name);

// The locations the API will accept as a destination, in display order. GET /locations lists
// every location the caller is a member of, Pending, Rejected and Suspended ones included, and
// flags each row `isShipToEligible` with ShipToLocationResolver's own predicate (#404): approved,
// active, organisation active and in good standing, subscription paid up. Naming any other row
// is a 404 on every read. The flag is the whole rule: the client keeps no copy of it, because a
// lapsed or suspended organisation is invisible on the row itself. A row without the flag (a
// response from before it) is not eligible.
export const eligibleShipToLocationsAtom = atom<LocationResponse[]>((get) =>
    get(locationsAtom)
        .filter((location) => location.isShipToEligible === true)
        // filter() already returned a fresh array — sort() is not mutating locationsAtom's.
        .sort(compareByCreatedAtThenName),
);

// The caller's reads are answered UNFILTERED: a platform admin, for whom shipToLocationIdAtom
// never sends an id (see there) and the API flags no row as the default. There is no destination
// to name, so the header's switcher is not shown at all.
export const browsesUnfilteredAtom = atom<boolean>(
    (get) => get(currentUserAtom)?.permissions?.includes(RoleType.Admin) ?? false,
);

// #417. The member is seated on no location (membershipStatus 7, NoLocationMembership) — the one
// definition every no-location surface reads, so no caller can forget to pass it down.
//
// The status comes from GET /users/me, read once per session; the location list is re-read by
// Checkout and other surfaces. GET /locations is membership-filtered, so an ELIGIBLE location in
// it is fresher evidence than the status: the organisation's admin has assigned one since. That
// evidence wins — no-location copy must never sit beside a destination the page is showing. An
// empty list (not yet loaded, or loaded and empty) is no evidence either way, and the status
// stands. Eligible is the API's flag (#404): a listed location that is Pending, or whose
// organisation has lapsed, can answer no read, so it is not evidence and the status stands too.
//
// Here and not in userAtoms.ts: this atom reads the eligible list, and this file already imports
// userAtoms.ts, so defining it there would make the two files import each other.
export const hasNoLocationMembershipAtom = atom<boolean>(
    (get) =>
        get(currentUserAtom)?.membershipStatus ===
            MembershipStatus.NoLocationMembership &&
        get(eligibleShipToLocationsAtom).length === 0,
);

// The destination to LABEL — always the one the requests are ANSWERED for. Keyed on the id
// actually SENT (shipToLocationIdAtom), not the stored one, so a stored id that is never sent
// (an admin's) is never named. With nothing sent, it is the row the API flags `isDefaultShipTo`:
// the location a request that carries no id is answered for (Lead ruling 2, option A; #404).
// Naming it rather than "—" is deliberate: the catalogue, the cart's restriction flags and the
// shipping quote are already computed for it, so the label is the one thing that could disagree
// with the page. A stale stored id is cleared by the switcher once the loaded list proves it
// gone, so that is a one-load state.
//
// Undefined until the list first loads (nothing is eligible yet), for an admin (no row is the
// default; reads are unfiltered) and for a caller with no eligible location (reads fail closed).
//
// The flag is a snapshot taken when GET /locations ran. If an approval, a membership change or a
// subscription lapse moves the API's default before the list is next read, the label names the
// old one until then.
export const activeShipToLocationAtom = atom<LocationResponse | undefined>(
    (get) => {
        const eligible = get(eligibleShipToLocationsAtom);
        const sentId = get(shipToLocationIdAtom);
        const sent = sentId
            ? eligible.find((location) => location.id === sentId)
            : undefined;

        return sent ?? eligible.find((location) => location.isDefaultShipTo);
    },
);

// The id actually SENT. Null means "send nothing and let the API pick", which is the correct
// request in three cases:
//
//   nothing stored          — the default (see activeShipToLocationAtom).
//   stored but not eligible — the location was removed, rejected or deactivated since it was
//                             stored, or its organisation lapsed or was suspended (the API's
//                             isShipToEligible). Sending it would 404 every read on the page at
//                             once.
//   the caller is an admin  — load-bearing, not defensive. useProductApi.getProducts/getProduct
//                             are shared by /a/inventory and /c, and ShipToLocationResolver
//                             bypasses an admin ONLY when no id is supplied: an admin who NAMES
//                             a location is filtered for it, so a customer id left in this
//                             admin's localStorage would silently drop rows from the inventory
//                             grid and zero the prices on the edit form. Suppressed once here
//                             rather than at every call site.
export const shipToLocationIdAtom = atom<string | null>((get) => {
    if (get(browsesUnfilteredAtom)) {
        return null;
    }

    const storedId = get(storedShipToLocationIdAtom);

    if (!storedId) {
        return null;
    }

    // The eligibility test needs the list, and locationsAtom starts EMPTY — GET /locations is
    // async and the header's switcher only issues it as ApprovalGate releases the page, in the
    // same commit the page fires its own first reads. Filtering against a list that has not
    // arrived is not "the stored id is ineligible", it is "no evidence either way", and
    // answering it `null` discarded the shopper's own choice on every hard page load: the first
    // reads were answered for the API's fallback destination and rendered, then re-issued when
    // the list landed. Two answers per load and a visible flip of the catalogue.
    //
    // So: no evidence, honour the choice. The cost is bounded and self-correcting — a stored id
    // that HAS become ineligible resolves NotFound at the API (ShipToLocationResolver: a named
    // location that is not the caller's is an error the caller must see), and one commit later
    // the list lands, the clause below drops the id and every read re-issues without it.
    if (!get(locationsLoadedAtom)) {
        return storedId;
    }

    return get(eligibleShipToLocationsAtom).some(
        (location) => location.id === storedId,
    )
        ? storedId
        : null;
});
